import { Export } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "react-toastify";

import { SplitButton } from "common/components/SplitButton";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { get } from "common/helpers/HTTP";
import { formatNumber } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  CrmType,
  EmailOutreachType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { INTEGRATIONS_MAP } from "../../../../integrations/constants";
import useLeadExports from "../../../datahooks/useLeadExports";
import OutreachExportModal from "./OutreachExportModal";

import InstantlyIcon from "assets/icons/instantly.svg?react";
import HubspotIcon from "assets/icons/integrations/hubspot-simple.svg?react";
import PipedriveIcon from "assets/icons/integrations/pipedrive-simple.svg?react";
import SmartleadIcon from "assets/icons/smartlead.svg?react";

const crmIntegrationExports = [
  { key: "hubspot", icon: HubspotIcon },
  { key: "pipedrive", icon: PipedriveIcon },
] as const;

const outreachIntegrationsExport = [
  { key: "instantly", icon: InstantlyIcon },
  { key: "smartlead", icon: SmartleadIcon },
] as const;

async function pollExportJob(
  workspaceId: string,
  jobId: string,
  integrationName: string,
) {
  const { job } = await get(
    `workspaces/${workspaceId}/integrations/jobs/${jobId}`,
  );
  if (job.state === "in_progress") {
    setTimeout(() => pollExportJob(workspaceId, jobId, integrationName), 8000);
  } else if (job.state === "success") {
    toast.success(`Data exported to ${integrationName} successfully`);
  } else if (job.state === "failed") {
    toast.error(
      `Data export to ${integrationName} failed with code ${job.code}`,
    );
  }
}

interface ExportLeadsProps {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  totalLeadsCount: number;
  isFetching: boolean;
}

export default function ExportLeads({
  searchParams,
  totalLeadsCount,
  isFetching,
}: ExportLeadsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportUrl, setExportUrl] = useState<string>();
  const [selectedOutreachType, setSelectedOutreachType] =
    useState<EmailOutreachType>();

  const { integrationSettings } = useIntegrationSettings();
  const {
    instantly,
    smartlead,
    rb2b: _rb2b,
    ...crmIntegrations
  } = integrationSettings || {};

  const isDisabled = isFetching || totalLeadsCount === 0;

  const hasConnectedOutreachIntegrations = instantly || smartlead;
  const hasInstalledCrmIntegrations = Object.values(crmIntegrations).some(
    (settingsItem) => !("install_url" in settingsItem),
  );

  const {
    exportLeads,
    exportLeadsToCrmIntegration,
    isExportingLeads,
    isExportingLeadsToCrmIntegration,
  } = useLeadExports();

  function onExport() {
    exportLeads({ searchParams }).then((url) => {
      setExportUrl(url);
    });

    toast("Your export is preparing, you will be notified when it's ready");
  }

  function onCrmIntegrationExport(crmType: CrmType, name: string) {
    exportLeadsToCrmIntegration({ searchParams, crmType })
      .then((jobId) => {
        if (jobId) {
          // poll the job to show a notification when it's finished
          pollExportJob(workspaceId, jobId, name);
        } else {
          toast.success(`Data exported to ${name} successfully`);
        }
      })
      .catch(() => {
        toast.error(`Couldn't export data to ${name}`);
      });
    toast("Your export is preparing, you will be notified when it's ready");
  }

  const commonButtonProps = {
    variant: "secondary-purple",
    leftIcon: <Export weight="fill" />,
    disabled: isDisabled,
    onClick: onExport,
    isLoading: isExportingLeads,
    size: isDesktop ? "sm" : "md",
    children: (
      <>
        Export{" "}
        {isFetching ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          `(${formatNumber(totalLeadsCount)})`
        )}
      </>
    ),
  } as const;

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      {hasConnectedOutreachIntegrations || hasInstalledCrmIntegrations ? (
        <SplitButton
          {...commonButtonProps}
          dropdownMenuItems={[
            ...crmIntegrationExports
              .filter(({ key }) => !("install_url" in integrationSettings[key]))
              .map(({ key, icon }) => {
                const { title } = INTEGRATIONS_MAP[key];

                return {
                  label: `Export to ${title}`,
                  icon,
                  onSelect: () => onCrmIntegrationExport(key, title),
                  disabled: isExportingLeadsToCrmIntegration,
                };
              }),
            ...outreachIntegrationsExport
              .filter(({ key }) => integrationSettings[key])
              .map(({ key, icon }) => {
                if (integrationSettings[key]) {
                  return {
                    label: `Export to ${key}`,
                    icon,
                    onSelect: () => {
                      setSelectedOutreachType(key);
                      setIsModalOpen(true);
                    },
                  };
                }
              }),
          ]}
        />
      ) : (
        <Button {...commonButtonProps} />
      )}
      {hasConnectedOutreachIntegrations && (
        <OutreachExportModal
          key={selectedOutreachType}
          type={selectedOutreachType}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          searchParams={searchParams}
        />
      )}
      <Component
        open={!!exportUrl}
        onOpenChange={(open) => {
          if (!open) {
            setExportUrl(undefined);
          }
        }}
      >
        <Content>
          <Title>Your export is ready</Title>
          <Description className="mb-8">
            Click the button below to download your export in CSV format
          </Description>
          <Button
            size={isDesktop ? "md" : "lg"}
            onClick={() => {
              window.open(exportUrl);
              setExportUrl(null);
            }}
          >
            Download
          </Button>
        </Content>
      </Component>
    </>
  );
}
